import {
  CHALLENGE_CODE_VERSION, CHALLENGE_SEED_MAX, Challenge, ChallengeData, ChallengeRead, ChallengeRecord, LevelModifiers, RecordNote,
  challengeModifiers, decodeChallengeCode, encodeChallenge, normalisedPool, readChallenge, utcDay,
} from "@highmarch/domain";
import { ticksToMs } from "./achievements";
import type { HallOfRecords } from "./records";
import type { LevelCatalog } from "./levelCatalog";

/**
 * Challenge codes (MP-28, docs/MODES.md §7) as use cases: read a code a friend sent (refused cleanly when it is
 * mistyped, from another version or names what this game lacks), make one from a level the player has cleared, the
 * run a code starts (the normalised profile with the code's towers and seed: app runProfile.ts), and what a finished
 * run records: the code's best in the Hall of Records. A code's run pays no resources, no chest and no embers; pass
 * XP comes from the battle like any other (features/season.ts).
 */

/** What a maker picks on the Create panel. */
export interface ChallengeDraft {
  readonly level: number;
  readonly seed: number;
  /** Code tower bits (meta/challengeCode.ts): the pool, and the banned towers within it. */
  readonly pool: number;
  readonly banned: number;
  readonly modifiers: readonly number[];
  readonly livesCap: number;
}

/** A finished challenge run, as the end screen reports it. `key` identifies the run (it counts once). */
export interface FinishedChallengeRun {
  readonly modifiers: LevelModifiers;
  readonly level: number;
  readonly won: boolean;
  readonly ticks: number;
  readonly stagesCleared: number;
  readonly key: string;
  readonly now: number;
}

/** What it recorded: the code's best before and after, and whether it beat it. */
export interface ChallengeOutcome {
  readonly code: string;
  readonly won: boolean;
  readonly ms: number;
  readonly note: RecordNote<ChallengeRecord>;
  readonly best: ChallengeRecord | null;
}

export class ChallengeCodes {
  constructor(private readonly catalog: LevelCatalog, private readonly hall: HallOfRecords) {}

  get data(): ChallengeData { return this.catalog.balance.modes.challenge; }
  /** Opens with Daily Road (the data's unlockAfterLevel). */
  get open(): boolean { return this.catalog.highestLevelCleared >= this.data.unlockAfterLevel; }
  get unlockAfterLevel(): number { return this.data.unlockAfterLevel; }

  /** Reads a code the player typed or pasted. */
  read(text: string): ChallengeRead { return readChallenge(this.catalog.balance, text); }

  /** What a code starts: the layout it borrows and the run's modifiers (the seed and towers follow from them). */
  run(c: Challenge): { readonly level: number; readonly modifiers: LevelModifiers } {
    return { level: c.level, modifiers: challengeModifiers(this.catalog.balance, c) };
  }

  /** Whether `modifiers` are a challenge code's run. */
  isRun(modifiers: LevelModifiers): boolean { return modifiers.challenge !== undefined; }

  /** The levels a maker may pick: cleared ones from the data's `levelFrom`, highest first. */
  levels(): number[] {
    const out: number[] = [];
    for (let l = this.catalog.highestLevelCleared; l >= this.data.levelFrom; l--) if (this.catalog.isCleared(l)) out.push(l);
    return out;
  }

  /** The Create panel's starting draft: the highest cleared level, today's seed, the normalised towers, no twist. */
  draft(now: number): ChallengeDraft {
    return { level: this.levels()[0] ?? this.data.levelFrom, seed: this.daySeed(now), pool: normalisedPool(this.catalog.balance), banned: 0, modifiers: [], livesCap: 0 };
  }

  /** "From the day": the UTC day number as the seed, so a code made today with the day's seed reads the same for everyone. */
  daySeed(now: number): number { return utcDay(now) & CHALLENGE_SEED_MAX; }

  /** Makes the draft's code: the same check as a code read from a friend (a level not cleared is refused too). */
  make(d: ChallengeDraft): ChallengeRead {
    if (!this.levels().includes(d.level)) return { ok: false, error: "level", message: "Pick a level you have cleared." };
    const c: Challenge = { version: CHALLENGE_CODE_VERSION, ...d, banned: d.banned & d.pool, modifiers: [...d.modifiers].sort((a, b) => a - b) };
    try { return readChallenge(this.catalog.balance, encodeChallenge(c)); }
    catch { return { ok: false, error: "modifier", message: "Pick at most two different twists." }; }
  }

  /** The codes played, newest first, with their bests (the Hall's). */
  recent(): readonly ChallengeRecord[] { return this.hall.state.challenges; }

  /** A run ended: the code's best in the Hall. Null for a run that is not a challenge's. Counted once per `key`. */
  finishRun(r: FinishedChallengeRun): ChallengeOutcome | null {
    const code = r.modifiers.challenge?.code;
    const parsed = code ? decodeChallengeCode(code) : null;
    if (!code || !parsed?.ok) return null;
    const ms = r.won ? ticksToMs(r.ticks) : 0;
    const note = this.hall.noteChallenge({ code, level: r.level, won: r.won, ms, stages: r.stagesCleared, key: r.key, at: r.now });
    return { code, won: r.won, ms, note, best: this.hall.state.challenge(code) };
  }
}
